/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchLoanReports } from "../redux/slices/loanReportSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/LoanReport.css";
import { jsPDF } from "jspdf";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { Download, FileTextIcon } from "lucide-react";


const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50,100, "All"];
const API_URL = "https://payyug.in/api/"; // replace with your API base

const LoanDisbursementReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loans, loading, error } = useSelector((state) => state.loanReport);
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;

  const [filterData, setFilterData] = useState({
    fromDate: "",
    toDate: "",
    loanAccountNumber: "",
    userId: "",
    userName: "",
    loanStatus: "",
    loanType: "",
  });

  const [filteredLoans, setFilteredLoans] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
    const [company, setCompany] = useState(null);


  useEffect(() => {
    dispatch(fetchLoanReports());
  }, [dispatch]);

  useEffect(() => {
    // 🔹 Only include disbursed loans
    const disbursedLoans = loans.filter(
      (loan) => loan.status?.toLowerCase() === "disbursed"
    );
    setFilteredLoans(disbursedLoans);
    setCurrentPage(1);
  }, [loans]);

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error}`);
    }
  }, [error]);

  const handleChange = (e) => {
    setFilterData({ ...filterData, [e.target.name]: e.target.value });
  };


   useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`,
        );
        const data = await response.json();

        if (response.ok && data) {
          // Dynamically construct logo URL
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        } else {
          console.error("Failed to fetch company data");
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      }
    };

    if (superAdminId) fetchCompany();
  }, [superAdminId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const filtered = loans
      .filter((loan) => loan.status?.toLowerCase() === "disbursed")
      .filter((loan) => {
        const {
          fromDate,
          toDate,
          loanAccountNumber,
          userId,
          userName,
          loanStatus,
          loanType,
        } = filterData;
        const appliedDate = new Date(loan.appliedDate);

        return (
          (!fromDate || appliedDate >= new Date(fromDate)) &&
          (!toDate || appliedDate <= new Date(toDate)) &&
          (!loanAccountNumber ||
            loan.loanAccountNumber
              ?.toLowerCase()
              .includes(loanAccountNumber.toLowerCase())) &&
          (!userId || loan.userId?.toString() === userId) &&
          (!userName ||
            loan.userName?.toLowerCase().includes(userName.toLowerCase())) &&
          (!loanStatus ||
            loan.status?.toLowerCase() === loanStatus.toLowerCase()) && 
          (!loanType || loan.loanType?.toLowerCase() === loanType.toLowerCase())
        );
      });

    setFilteredLoans(filtered);
    setCurrentPage(1);
  };

const handleItemsPerPageChange = (e) => {
  const val = e.target.value;
  setItemsPerPage(val === "All" ? "All" : Number(val));
  setCurrentPage(1);
};

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")} ${String(
      date.getHours()
    ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

const isShowAll = itemsPerPage === "All";
const effectiveItemsPerPage = isShowAll ? filteredLoans.length : Number(itemsPerPage);

const totalPages = isShowAll || filteredLoans.length === 0 
  ? 1 
  : Math.ceil(filteredLoans.length / effectiveItemsPerPage);

const startIndex = isShowAll ? 0 : (currentPage - 1) * effectiveItemsPerPage;

const currentLoans = isShowAll 
  ? filteredLoans 
  : filteredLoans.slice(startIndex, startIndex + effectiveItemsPerPage);


   const handleDownloadPDF = async () => {
            const doc = new jsPDF("p", "mm", "a4");
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
        
            // ================= LOGO =================
            const logoX = 15;
            const logoY = 12;
            const logoSize = 25;
            const logoPadding = 2;
        
            // Border color & thickness
            doc.setDrawColor(0);
            doc.setLineWidth(0.2);
        
            // Calculate center of logo
            const centerX = logoX + logoSize / 2;
            const centerY = logoY + logoSize / 2;
        
            // Radius = half of logo + padding
            const radius = logoSize / 2 + logoPadding;
        
            // Draw circle
            doc.circle(centerX, centerY, radius);
        
            // Logo image
            if (company?.logoUrl) {
              try {
                const logoResp = await fetch(company.logoUrl);
                const logoBlob = await logoResp.blob();
                const reader = new FileReader();
                await new Promise((resolve) => {
                  reader.onload = () => {
                    doc.addImage(
                      reader.result,
                      "PNG",
                      logoX,
                      logoY,
                      logoSize,
                      logoSize,
                    );
                    resolve();
                  };
                  reader.readAsDataURL(logoBlob);
                });
              } catch {
                // silent fail
              }
            }
        
            // ================= COMPANY NAME (AUTO WRAP) =================
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
        
            const nameX = logoX + logoSize + 5;
            const nameY = 18;
            const nameMaxWidth = pageWidth - nameX - 15;
        
            const companyNameText = doc.splitTextToSize(
              (company?.name || "COMPANY NAME").toUpperCase(),
              nameMaxWidth,
            );
        
            doc.text(companyNameText, nameX, nameY);
        
            // Calculate Y after company name
            let currentY = nameY + companyNameText.length * 6;
        
            // ================= COMPANY DETAILS =================
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
        
            const detailX = nameX;
            const detailMaxWidth = nameMaxWidth;
        
            // Address
            const addressText = doc.splitTextToSize(
              company?.address || "Company Address",
              detailMaxWidth,
            );
            doc.text(addressText, detailX, currentY);
            currentY += addressText.length * 5;
        
            // Email
            const emailText = doc.splitTextToSize(
              `Email: ${company?.email || "-"}`,
              detailMaxWidth,
            );
            doc.text(emailText, detailX, currentY);
            currentY += emailText.length * 5;
        
            // Phone
            const phoneText = doc.splitTextToSize(
              `Phone: ${company?.contact || "-"}`,
              detailMaxWidth,
            );
            doc.text(phoneText, detailX, currentY);
            currentY += phoneText.length * 5;
        
            // Divider
            doc.line(10, currentY + 4, pageWidth - 10, currentY + 4);
        
            // ================= REPORT TITLE =================
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.text("Disbursed Loan Report", pageWidth / 2, currentY + 14, {
              align: "center",
            });
        
            const tableColumn = [
              "SN",
              "Customer Name",
              "Account No",
              "Loan Type",
              "Loan Amount",
              "Tenure",
              "Recovery Freq.",
              "Emi Amount",
              "Applied Date",
              "Disbursal Date",
              "First Emi Date",
              "Status",
            ];
        
            const tableRows = currentLoans.map((item, index) => [
              index + 1,
              item?.userName || "N/A",
              item?.loanAccountNumber || "N/A",
              item?.loanType?`Loan (${item?.loanType})`:"N/A",
              item?.loanAmount ? item?.loanAmount : 0,
              item?.loanTenure ? item?.loanTenure : "N/A",
              item?.loanRecoveryFrequency ? item?.loanRecoveryFrequency : "N/A",
              item.loanEmiAmount ? item.loanEmiAmount : 0,
              item.loanAppliedDate ? new Date(item.loanAppliedDate).toLocaleDateString("en-GB") : "N/A",
              item.disbursalDate ? new Date(item.disbursalDate).toLocaleDateString("en-GB") : "N/A",
              item.firstEmiDate ? new Date(item.firstEmiDate).toLocaleDateString("en-GB") : "N/A",
              item.status || "N/A",
            ]);
        
            autoTable(doc, {
              startY: currentY + 20,
              head: [tableColumn],
              body: tableRows,
        
              theme: "grid",
              styles: {
                fontSize: 7,
                cellPadding: 3,
                halign: "center",
              },
              headStyles: {
                fontStyle: "bold",
                fontSize: 7,
              },
              alternateRowStyles: {
                fillColor: [245, 245, 245],
              },
        
              didDrawPage: function () {
                const pageWidth = doc.internal.pageSize.width;
                const pageHeight = doc.internal.pageSize.height;
        
                doc.setDrawColor(0);
                doc.setLineWidth(0.2);
        
                // Page border on EVERY page
                doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
              },
            });
        
            // ================= FOOTER =================
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
              doc.setPage(i);
              doc.setFontSize(8);
              doc.setTextColor(100);
              doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
              doc.text("Generated by Loan System", 14, pageHeight - 10);
            }
        
            doc.save("Disbursed_Loan_Report.pdf");
          };
        
          const exportExcel = () => {
            const data = currentLoans.map((item) => ({
              "Customer Name": item?.userName || "N/A",
              "Account No": item?.loanAccountNumber || "N/A",
              "Loan Type": item?.loanType?`Loan (${item?.loanType})`:"N/A",
              "Loan Amount": item?.loanAmount
                ? item?.loanAmount
                : 0,
              "Loan Tenure": item?.loanTenure ? item?.loanTenure : "N/A",
              "Recovery Frequency": item?.loanRecoveryFrequency ? item?.loanRecoveryFrequency : "N/A",
              "Emi Amount": item.loanEmiAmount ? item.loanEmiAmount : 0,
              "Applied Date": item.loanAppliedDate
                ? new Date(item.loanAppliedDate).toLocaleDateString("en-GB")
                : "N/A",
              "Disbursal Date": item.disbursalDate
                ? new Date(item.disbursalDate).toLocaleDateString("en-GB")
                : "N/A",
              "First Emi Date": item?.firstEmiDate
                ? new Date(item?.firstEmiDate).toLocaleDateString("en-GB")
                : "N/A",
            Status: item.status || "N/A",
            }));
        
            const worksheet = XLSX.utils.json_to_sheet(data);
        
            // 🔹 Header styling
            const headerCells = Object.keys(data[0]);
            headerCells.forEach((_, index) => {
              const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
              worksheet[cellRef].s = {
                font: {
                  bold: true,
                  color: { rgb: "FFFFFF" },
                },
                fill: {
                  fgColor: { rgb: "1F4E78" },
                },
                alignment: {
                  horizontal: "center",
                  vertical: "center",
                },
              };
            });
        
            // 🔹 Auto column width
            worksheet["!cols"] = headerCells.map((key) => ({
              wch: Math.max(key.length + 5, 18),
            }));
        
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Loan Report");
        
            XLSX.writeFile(workbook, "Disbursed_Loan_Report.xlsx");
          };


  return (
    <div className="LoanReport-container">
      {/* Filter Section */}
      <section className="LoanReport-filterSection">
        <h2 className="LoanReport-title">Disbursed Loan Report Filter</h2>
        <p className="LoanReport-subtitle">
          Use filter to see more precise report
        </p>
        <form className="LoanReport-form" onSubmit={handleSubmit}>
          <div className="LoanReport-grid">
            <div className="LoanReport-formGroup">
              <label>From Date</label>
              <input type="date" name="fromDate" onChange={handleChange} />
            </div>
            <div className="LoanReport-formGroup">
              <label>To Date</label>
              <input type="date" name="toDate" onChange={handleChange} />
            </div>
            <div className="LoanReport-formGroup">
              <label>Loan Account Number</label>
              <input
                type="text"
                name="loanAccountNumber"
                placeholder="Loan account number"
                onChange={handleChange}
              />
            </div>
            {/* <div className="LoanReport-formGroup">
              <label>Applicant User ID</label>
              <input
                type="text"
                name="userId"
                placeholder="Applicant user id"
                onChange={handleChange}
              />
            </div> */}
            <div className="LoanReport-formGroup">
              <label>Applicant User Name</label>
              <input
                type="text"
                name="userName"
                placeholder="Applicant user name"
                onChange={handleChange}
              />
            </div>
            <div className="LoanReport-formGroup">
              <label>Choose Loan Status</label>
              <select name="loanStatus" onChange={handleChange}>
                <option value="">Select loan status</option>
                <option value="disbursed">Disbursed</option>
              </select>
            </div>
            <div className="LoanReport-formGroup">
              <label>Loan Type</label>
              <select name="loanType" onChange={handleChange}>
                <option value="">All Loan Types</option>
                <option value="Fixed">Fixed</option>
                <option value="Personal">Personal</option>
              </select>
            </div>
          </div>
          <button type="submit" className="LoanReport-submitBtn">
            Submit
          </button>
        </form>
      </section>

      {/* Loan Details Section */}
      <section className="LoanReport-detailsSection">
        <div className="LoanReport-tableHeader">
          <div>
            <label>Show </label>
                        <select onChange={handleItemsPerPageChange} value={itemsPerPage}>
  {ITEMS_PER_PAGE_OPTIONS.map((count) => (
    <option key={count} value={count}>
      {count}
    </option>
  ))}
</select>
            <span> entries</span>
          </div>
          {/* <input
            type="text"
            placeholder="Search..."
            className="LoanReport-searchInput"
          /> */}
          {currentLoans.length > 0 && (
                    <div
                      className=""
                      style={{ display: "flex", gap: "5px", marginTop: "10px" }}
                    >
                      <button
                        className="collection-report-filter-btn"
                        style={{ display: "flex", alignItems: "center", gap: "5px" }}
                        onClick={exportExcel}
                      >
                        <FileTextIcon size={18} /> Excel
                      </button>
                      <button
                        className="collection-report-filter-btn"
                        style={{ display: "flex", alignItems: "center", gap: "5px" }}
                        onClick={handleDownloadPDF}
                      >
                        <Download size={18} /> PDF
                      </button>
                    </div>
                  )}
        </div>

        <div className="LoanReport-tableContainer">
          <table className="LoanReport-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Loan ID</th>
                <th>Borrower Name</th>
                <th>Loan Amount</th>
                <th>Interest Amount</th>
                <th>Loan Tenure</th>
                <th>Loan Frequency</th>
                <th>Applied Date</th>
                <th>Approval Date</th>
                <th>Disbursal Date</th>
                <th>Loan Status</th>
                <th>EMI Amount</th>
                <th>Total Payable</th>
                <th>Loan Type</th>
                <th>View EMI</th>
                <th>Loan Card</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="22">Loading...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="22">Error: {error}</td>
                </tr>
              ) : currentLoans.length === 0 ? (
                <tr>
                  <td colSpan="22">No data available</td>
                </tr>
              ) : (
                currentLoans.map((loan, index) => (
                  <tr key={loan.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>{loan.loanAccountNumber || "N/A"}</td>
                    <td>{loan.userName || "N/A"}</td>
                    <td>{loan.loanAmount || "N/A"}</td>
                    <td>{loan.loanInterest || "N/A"}</td>
                    <td>{loan.loanTenure || "N/A"}</td>
                    <td>{loan.loanRecoveryFrequency || "N/A"}</td>
                    <td>{new Date(loan.loanAppliedDate).toLocaleDateString("en-GB")||"N/A"}</td>
                    <td>{new Date(loan.firstEmiDate).toLocaleDateString("en-GB")||"N/A"}</td>
                    <td>{new Date(loan.disbursalDate).toLocaleDateString("en-GB")||"N/A"}</td>
                    <td>{loan.status || "N/A"}</td>
                    <td>{loan.loanEmiAmount || "N/A"}</td>
                    <td>{loan.loanPayable || "N/A"}</td>
                    <td>{loan.loanType || "N/A"}</td>
                    <td>
                      <button
                        onClick={() => navigate(`/ViewEmiLoanData/${loan.id}`,{state:loan})}
                      >
                        View EMI
                      </button>
                    </td>
                    <td>
                      <button
                        onClick={() => navigate(`/LoanCardPage/${loan.id}`)}
                      >
                        Loan Card
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="LoanReport-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default LoanDisbursementReport;
